<template>
  <div ref="chartContainer" style="width: 100%; height: 400px;"></div>
</template>

<script>
import * as echarts from 'echarts';

export default {
  name: 'MultiLineChart',
  props: {
    // 接收多维数据集 [{name: '系列1', data: [...]}, {name: '系列2', data: [...]}]
    chartSeries: {
      type: Array,
      required: true,
      validator(value) {
        // 校验每个系列必须包含 name 和 data 字段
        return value.every(item => 'name' in item && 'data' in item);
      }
    },
    // X轴标签（可选，若未提供则使用索引）
    xAxisLabels: {
      type: Array,
      default: () => []
    },
    // 图表标题（可选）
    titleText: {
      type: String,
      default: '多条折线图对比'
    }
  },
  data() {
    return {
      chartInstance: null
    };
  },
  mounted() {
    this.initChart();
    window.addEventListener('resize', this.handleResize);
  },
  beforeDestroy() {
    if (this.chartInstance) {
      this.chartInstance.dispose();
      this.chartInstance = null;
    }
    window.removeEventListener('resize', this.handleResize);
  },
  methods: {
    initChart() {
      const chartDom = this.$refs.chartContainer;
      this.chartInstance = echarts.init(chartDom);

      // 构建完整配置项
      const option = {
        title: {
          text: this.titleText,
          left: 'center',
          textStyle: { color: '#333', fontSize: 16 }
        },
        tooltip: {
          trigger: 'axis',
          formatter: params => {
            // 格式化提示框内容（显示当前坐标点的多条数据线值）
            let html = `${params[0].axisValueLabel}<br/>`;
            params.forEach(param => {
              html += `<div style="display:flex;"><div style="margin:8px 8px 0px 0px;border-radius: 3.5px;width:19px;height:7px;background:${this.generateColor(param.seriesName)}"></div><div>${param.seriesName}: ${param.value}</div></div> <br/>`;
            });
            return html;
          }
        },
        legend: {
          data: this.chartSeries.map(series => series.name), // 自动生成图例
          bottom: 10
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '15%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          boundaryGap: false,
          data: this.xAxisLabels.length ? this.xAxisLabels : this.chartSeries[0].data.map((_, i) => `第${i + 1}期`), // 若无自定义X轴标签则用索引
          axisLine: { lineStyle: { color: '#999' } }
        },
        yAxis: {
          type: 'value',
          name: this.$t("ConsolePanel.text18"),
          nameLocation: 'end',
          splitLine: { show: true, lineStyle: { type: 'dashed', color: ['#eee'] } }
        },
        series: this.chartSeries.map(series => ({
          name: series.name,
          type: 'line',
          smooth: true,
          symbol: 'circle',
          symbolSize: 8,
          data: series.data,
          itemStyle: { color: this.generateColor(series.name) }, // 自动生成区分度较高的颜色
          lineStyle: { width: 3 }
        }))
      };

      this.chartInstance.setOption(option);
    },
    handleResize() {
      if (this.chartInstance) this.chartInstance.resize();
    },
    // 生成基于名称哈希的颜色（避免重复）
    generateColor(name) {
      const hash = parseInt(name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0));
      return `hsl(${hash % 360}, 70%, 60%)`; // 使用 HSL 色彩空间保证亮度一致
    }
  },
  watch: {
    chartSeries: {
      handler(newVal) {
        if (this.chartInstance) {
          const option = this.chartInstance.getOption();
          // 更新所有系列的数据
          newVal.forEach((series, index) => {
            option.series[index].data = series.data;
            option.series[index].name = series.name;
            option.series[index].itemStyle.color = this.generateColor(series.name);
          });
          // 更新 X 轴标签（如果提供了新的标签）
          if (this.xAxisLabels.length) {
            option.xAxis[0].data = this.xAxisLabels;
          }
          this.chartInstance.setOption(option);
        }
      },
      deep: true
    },
    xAxisLabels(newVal) {
      if (this.chartInstance && newVal.length) {
        const option = this.chartInstance.getOption();
        option.xAxis[0].data = newVal;
        this.chartInstance.setOption(option);
      }
    }
  }
};
</script>

<style scoped>
.chart-container {
  background-color: #fff;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}
</style>
